/* RaceWerkz platform inquiry v5 -> Cloudflare Worker. No credentials in storefront code. */
(() => {
  // This asset supports multiple independent section instances and theme editor reloads.
  if (window.__racewerkzPlatformInquiryJS) return;
  window.__racewerkzPlatformInquiryJS = true;

  let turnstileScriptPromise;
  function loadTurnstile() {
    if (window.turnstile?.render) return Promise.resolve(window.turnstile);
    if (!turnstileScriptPromise) {
      turnstileScriptPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.onload = () => window.turnstile?.render ? resolve(window.turnstile) : reject(new Error('Verification not available'));
        script.onerror = () => reject(new Error('Verification could not load'));
        document.head.appendChild(script);
      });
    }
    return turnstileScriptPromise;
  }

  // Accept US 10-digit numbers; optional leading 1 is stripped on input.
  // Send the exact (XXX) XXX-XXXX format Shopmonkey uses, never a +1 prefix.
  function normalizeUsPhone(raw) {
    const value = String(raw || '').trim();
    if (!value) return '';
    if (!/^[+\d().\-\s]+$/.test(value) || (value.includes('+') && !value.startsWith('+1'))) return null;
    const digits = value.replace(/\D/g, '');
    const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
    return /^[2-9]\d{2}[2-9]\d{6}$/.test(national)
      ? `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}` : null;
  }

  function formatPhoneAsTyped(input) {
    const raw = input.value;
    const allDigits = raw.replace(/\D/g, '');
    const hasCountryCode = allDigits.length === 11 && allDigits.startsWith('1');
    const digits = hasCountryCode ? allDigits.slice(1) : allDigits;
    // Don't silently discard excess/invalid digits; validation can explain the problem.
    if (digits.length > 10 || (allDigits.length === 11 && !hasCountryCode)) return;
    const previousCaret = input.selectionStart ?? raw.length;
    let digitsBeforeCaret = raw.slice(0, previousCaret).replace(/\D/g, '').length;
    if (hasCountryCode && digitsBeforeCaret > 0) digitsBeforeCaret--;
    let display = '';
    if (digits.length > 0) display = '(' + digits.slice(0, 3);
    if (digits.length >= 4) display += ') ' + digits.slice(3, 6);
    if (digits.length >= 7) display += '-' + digits.slice(6, 10);
    if (display === raw) return;
    input.value = display;
    if (document.activeElement === input && input.setSelectionRange) {
      let target = display.length;
      if (previousCaret < raw.length) {
        target = digitsBeforeCaret === 0 ? Math.min(1, display.length) : display.length;
        let seen = 0;
        for (let i = 0; i < display.length; i++) {
          if (/\d/.test(display[i])) seen++;
          if (seen === digitsBeforeCaret) { target = i + 1; break; }
        }
      }
      input.setSelectionRange(target, target);
    }
  }

  function setStatus(node, text, kind = '') {
    node.textContent = text;
    if (kind) node.dataset.state = kind;
    else delete node.dataset.state;
  }

  function init(root = document) {
    root.querySelectorAll('form[data-rwz-form]:not([data-rwz-ready])').forEach((form) => {
      form.dataset.rwzReady = 'true';
      const status = form.querySelector('[data-rwz-status]');
      const button = form.querySelector('button[type=submit]');
      const verification = form.querySelector('[data-rwz-turnstile]');
      const originalButtonText = button.textContent;
      const sitekey = form.dataset.turnstileSitekey?.trim() || '';
      const state = { widgetId: null, token: '', completed: false, uncertain: false, submitting: false, awaitingAttribution: false };
      const phoneInput = form.querySelector('[data-rwz-phone]');
      const smsOptInInput = form.querySelector('[data-rwz-sms-opt-in]');
      function syncPhoneAndSms() {
        if (!phoneInput) return;
        const canonical = normalizeUsPhone(phoneInput.value);
        phoneInput.setCustomValidity(phoneInput.value.trim() && !canonical
          ? 'Enter a valid 10-digit US phone number.' : '');
        if (smsOptInInput) {
          smsOptInInput.disabled = !canonical;
          if (!canonical) smsOptInInput.checked = false;
        }
      }
      if (phoneInput) {
        phoneInput.addEventListener('input', () => { formatPhoneAsTyped(phoneInput); syncPhoneAndSms(); });
        phoneInput.addEventListener('blur', () => { formatPhoneAsTyped(phoneInput); syncPhoneAndSms(); });
        syncPhoneAndSms();
      }

      if (!sitekey) {
        setStatus(status, 'Verification is not configured. Please contact us directly.', 'error');
      } else {
        loadTurnstile().then((turnstile) => {
          if (!form.isConnected) return;
          state.widgetId = turnstile.render(verification, {
            sitekey,
            appearance: 'interaction-only', // Removes idle/unnecessary widget chrome.
            theme: 'dark',
            size: 'flexible',
            'offlabel-show-help': false,
            'feedback-enabled': false,
            'response-field': false,
            callback: (token) => {
              state.token = token;
              if (!state.completed && !state.uncertain && !state.submitting) setStatus(status, '');
            },
            'expired-callback': () => { state.token = ''; },
            'timeout-callback': () => {
              state.token = '';
              if (!state.completed) setStatus(status, 'Verification timed out. Please try again.', 'error');
            },
            'error-callback': () => {
              state.token = '';
              if (!state.completed) setStatus(status, 'Verification could not complete. Please reload the page or contact us directly.', 'error');
            }
          });
          verification._rwziWidgetId = state.widgetId;
        }).catch(() => {
          setStatus(status, 'Verification could not load. Please reload the page or contact us directly.', 'error');
        });
      }

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (state.completed || state.uncertain || state.submitting || state.awaitingAttribution) return;
        setStatus(status, '');
        syncPhoneAndSms();
        if (!form.reportValidity()) return;
        const interests = [...form.querySelectorAll('input[name="interests"]:checked')].map((x) => x.value);
        // Both block categories are optional in Shopify's editor. If one or more
        // interest checkboxes exist, require a choice; removing ALL blocks is valid.
        if (form.dataset.requireInterests === 'true' && form.querySelector('input[name="interests"]') && !interests.length) {
          setStatus(status, 'Please select at least one area of interest.', 'error');
          return;
        }
        if (!state.token) {
          setStatus(status, 'Please complete the security verification before submitting.', 'error');
          return;
        }
        const endpoint = form.dataset.endpoint || '';
        if (!/^https:\/\/[^\s]+\/inquiry\/?(?:\?.*)?$/i.test(endpoint)) {
          setStatus(status, 'The inquiry form is not configured yet.', 'error');
          return;
        }
        const fields = new FormData(form);
        // Attribution snapshot is consent-gated by the sitewide tracker. The form still
        // operates when that script is absent; it just records attribution unavailable.
        let attribution = {
          consent: 'unavailable',
          submissionPage: window.location.origin + window.location.pathname
        };
        state.awaitingAttribution = true;
        try {
          // Consent API may be temporarily unavailable even after the page renders.
          // Briefly await it so we don't silently send an unattributed inquiry due
          // solely to initialization order. The request still submits on timeout.
          if (typeof window.RacewerkzAttribution?.whenReady === 'function') {
            await window.RacewerkzAttribution.whenReady(1200);
          }
          attribution = window.RacewerkzAttribution?.snapshot?.() || attribution;
        } catch { /* Privacy/storage restrictions must not block the customer's inquiry. */ }
        finally { state.awaitingAttribution = false; }
        const body = {
          firstName: fields.get('firstName') || '',
          lastName: fields.get('lastName') || '',
          email: fields.get('email') || '',
          phone: phoneInput ? normalizeUsPhone(fields.get('phone')) || '' : '',
          smsOptIn: Boolean(smsOptInInput && !smsOptInInput.disabled && smsOptInInput.checked),
          year: fields.get('year') || '',
          inquiryContext: form.dataset.inquiryContext === 'general' ? 'general' : 'platform',
          vehicle: form.dataset.inquiryContext === 'general' ? '' : (form.dataset.vehicle || ''),
          vehicleDetail: fields.get('vehicleDetail') || '',
          orderReference: fields.get('orderReference') || '',
          requestType: fields.get('requestType') || '',
          interests,
          message: fields.get('message') || '',
          website: fields.get('website') || '',
          turnstileToken: state.token,
          attribution
        };
        // Snapshot conversion classification with the submitted form values.
        // Avoid relabeling a support request if the dropdown changes while the
        // network request is pending. Unclassified GENERAL requests are not sales leads.
        const requestSelect = form.querySelector('select[name="requestType"]');
        const selectedRequestOption = requestSelect?.selectedOptions?.[0];
        const isGeneralContact = form.dataset.inquiryContext === 'general';
        // Older platform-page markup may omit data-is-lead; keep its historical
        // lead behavior. General contacts fail closed unless an option is
        // explicitly marked as a sales lead.
        const trackAsLead = form.dataset.trackAsLead !== 'false' &&
          (selectedRequestOption
            ? (selectedRequestOption.dataset.isLead === 'true' ||
               (!isGeneralContact && selectedRequestOption.dataset.isLead !== 'false'))
            : !isGeneralContact);
        state.submitting = true;
        button.disabled = true;
        button.textContent = 'Sending...';

        try {
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
          });
          const reply = await response.json();
          if (!response.ok || reply.success !== true || reply.accepted !== true || !reply.leadId) {
            if (reply.uncertain) {
              state.uncertain = true;
              setStatus(status, reply.error || 'We could not confirm receipt. Please contact us rather than resubmitting.', 'error');
              button.textContent = 'Please contact us';
            } else {
              setStatus(status, reply.error || 'Unable to send your inquiry. Please try again.', 'error');
            }
            return;
          }
          // Use the lead-eligibility snapshot captured when the request was sent.
          state.completed = true;
          form.reset();
          syncPhoneAndSms();
          setStatus(status, form.dataset.successMessage || 'Thanks! Your inquiry has been received.', 'success');
          button.textContent = 'Inquiry Sent';
          // Only Shopmonkey-confirmed responses with accepted=true and a unique lead ID
          // can create a conversion event. This excludes honeypot and error responses.
          // Do not send names, email, phone, free-text notes, or click IDs to tag managers.
          const cp = window.Shopify?.customerPrivacy;
          let analyticsAllowed = false;
          let marketingAllowed = false;
          try {
            analyticsAllowed = cp?.analyticsProcessingAllowed?.() === true;
            marketingAllowed = cp?.marketingAllowed?.() === true;
          } catch { /* fail closed */ }
          const conversion = {
            event: 'racewerkz_inquiry_success',
            event_id: String(reply.leadId),
            lead_id: String(reply.leadId),
            shopmonkey_reference: String(reply.shopmonkeyReference || ''),
            platform: form.dataset.inquiryContext === 'general' ? 'General contact' : (form.dataset.vehicle || ''),
            lead_type: 'website_inquiry',
            source: marketingAllowed && attribution.consent === 'granted'
              ? (attribution.lastNonDirectTouch?.source || attribution.lastTouch?.source || 'unknown')
              : 'unavailable',
            analytics_allowed: analyticsAllowed,
            marketing_allowed: marketingAllowed
          };
          // Event is made available to theme-based GTM (if configured). No network
          // request is sent by this script; third-party tags must obey consent flags.
          if (trackAsLead && (analyticsAllowed || marketingAllowed)) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push(conversion);
          }
          // Google Ads lead-only conversion: reuse an existing storefront gtag function.
          // Never load a second Google base tag here and never emit on click/error/spam.
          // Google Ads uses transaction_id to deduplicate repeated deliveries of this lead.
          // This request carries no customer contact details or customer-entered text.
          if (trackAsLead && marketingAllowed && typeof window.gtag === 'function') {
            try {
              window.gtag('event', 'conversion', {
                send_to: 'AW-11071825256/V7p5CObp8KMdEOjKup8p',
                transaction_id: String(reply.leadId),
                value: 1.0,
                currency: 'USD'
              });
            } catch (error) {
              // Conversion measurement must never change the confirmed inquiry outcome.
              console.warn('[RaceWerkz] Google lead conversion unavailable.');
            }
          }
          // Meta Ads Lead: target ONLY the existing RaceWerkz dataset/pixel.
          // The Shopify Meta channel remains responsible for PageView/Purchase and
          // its existing server-side events; do not load or initialize another pixel.
          // eventID matches the confirmed Shopmonkey lead ID for future deduplication
          // against an explicitly configured server-side Lead event (none is sent here).
          if (trackAsLead && marketingAllowed && typeof window.fbq === 'function') {
            try {
              window.fbq('trackSingle', '722271649282162', 'Lead', {
                content_name: 'RaceWerkz Shopmonkey Inquiry',
                value: 1.0,
                currency: 'USD'
              }, { eventID: String(reply.leadId) });
            } catch (error) {
              // Measurement must never change the confirmed work-request outcome.
              console.warn('[RaceWerkz] Meta Lead event unavailable.');
            }
          }
          // Shopify customer event: available to an optional lead-only custom pixel.
          // Publish only after Shopmonkey confirms acceptance and only when marketing
          // measurement is permitted. Shopify also enforces its pixel privacy settings.
          // Do not include PII, customer messages, phone numbers or ad click identifiers.
          if (trackAsLead && marketingAllowed) {
            try {
              if (typeof window.Shopify?.analytics?.publish === 'function') {
                window.Shopify.analytics.publish('racewerkz:inquiry_success', {
                  event_id: String(reply.leadId),
                  platform: form.dataset.inquiryContext === 'general' ? 'General contact' : (form.dataset.vehicle || ''),
                  lead_type: 'website_inquiry'
                });
              }
            } catch (error) {
              // Measurement must never interfere with a confirmed Shopmonkey lead.
              console.warn('[RaceWerkz] Shopify lead event unavailable.');
            }
          }
          // Local (non-network) application event also fires on confirmed success,
          // even if the visitor declined optional measurement.
          form.dispatchEvent(new CustomEvent('racewerkz:inquiry-success', {
            bubbles: true,
            detail: { leadId: String(reply.leadId), shopmonkeyReference: String(reply.shopmonkeyReference || ''), trackedAsLead: trackAsLead }
          }));
        } catch {
          // A network failure can occur after Shopmonkey has created a record; do not retry automatically.
          state.uncertain = true;
          setStatus(status, 'We could not confirm receipt. Please contact us directly rather than resubmitting.', 'error');
          button.textContent = 'Please contact us';
        } finally {
          state.submitting = false;
          if (!state.completed && !state.uncertain) {
            button.disabled = false;
            button.textContent = originalButtonText;
            state.token = '';
            if (state.widgetId !== null && window.turnstile?.reset) window.turnstile.reset(state.widgetId);
          }
        }
      });
    });
  }

  function start() { init(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-rwz-turnstile]').forEach((el) => {
      if (window.turnstile?.remove && el._rwziWidgetId !== undefined) window.turnstile.remove(el._rwziWidgetId);
    });
  });
})();
